/** @module @liquidefy/code-editor/workspace/workspace @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  WorkspaceState,
  WorkspaceId,
  WorkspaceLayout,
  PanelState,
  EditorState,
  TerminalState,
  EditorInstanceId,
  TerminalId,
  FindInFilesState,
  LayoutType,
  EditorResult,
  EditorEvent,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asWorkspaceId,
  now,
  emptyWorkspaceState,
  DEFAULT_WORKSPACE_LAYOUT,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 WORKSPACE CONFIG — creation configuration interface
// ═══════════════════════════════════════════════════════════════════════════════

/** Configuration used to create a new workspace. */
export interface WorkspaceConfig {
  readonly name: string;
  readonly rootFolders?: string[];
  readonly theme?: string;
  readonly layoutType?: LayoutType;
  readonly settings?: Record<string, unknown>;
}

/**
 * Create a new WorkspaceState from a config.
 * Uses default layout and empty collections for editors and terminals.
 */
export function createWorkspace(
  config: WorkspaceConfig
): EditorResult<WorkspaceState> {
  if (!config.name.trim()) {
    return err('WORKSPACE_INVALID_NAME', 'Workspace name cannot be empty');
  }

  const id = asWorkspaceId(generateId('ws'));
  const layout: WorkspaceLayout = {
    ...DEFAULT_WORKSPACE_LAYOUT,
    type: config.layoutType ?? DEFAULT_WORKSPACE_LAYOUT.type,
  };

  const state: WorkspaceState = {
    ...emptyWorkspaceState(id),
    name: config.name.trim(),
    rootFolders: config.rootFolders ?? [],
    layout,
    theme: config.theme ?? 'dark',
    settings: config.settings ?? {},
  };

  const events: EditorEvent[] = [{
    kind: 'created',
    domain: 'workspace',
    timestamp: now(),
    payload: { workspaceId: id, name: state.name },
  }];

  return ok(state, 'WORKSPACE_CREATED', `Workspace '${state.name}' created`, events);
}

/**
 * Look up a workspace from a collection by ID.
 */
export function getWorkspaceById(
  workspaces: WorkspaceState[],
  id: WorkspaceId
): EditorResult<WorkspaceState> {
  const found = workspaces.find(w => w.id === id);
  if (!found) return err('WORKSPACE_NOT_FOUND', `Workspace '${id}' not found`);
  return ok(found, 'WORKSPACE_FOUND', `Workspace '${id}' retrieved`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 EDITOR MANAGEMENT — add, remove, activate editors within workspace
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add an EditorState to the workspace.
 * Returns err if an editor with the same ID already exists.
 */
export function addEditor(
  state: WorkspaceState,
  editorState: EditorState
): EditorResult<WorkspaceState> {
  if (state.editors.some(e => e.id === editorState.id)) {
    return err('WORKSPACE_EDITOR_EXISTS', `Editor '${editorState.id}' already in workspace`);
  }

  const events: EditorEvent[] = [{
    kind: 'created',
    domain: 'editor',
    timestamp: now(),
    payload: { workspaceId: state.id, editorId: editorState.id },
  }];

  return ok(
    { ...state, editors: [...state.editors, editorState] },
    'WORKSPACE_EDITOR_ADDED',
    `Editor '${editorState.id}' added to workspace`,
    events
  );
}

/**
 * Remove an editor from the workspace by ID.
 * Returns err if the editor is not found.
 */
export function removeEditor(
  state: WorkspaceState,
  editorId: EditorInstanceId
): EditorResult<WorkspaceState> {
  if (!state.editors.some(e => e.id === editorId)) {
    return err('WORKSPACE_EDITOR_NOT_FOUND', `Editor '${editorId}' not found in workspace`);
  }

  const editors = state.editors.filter(e => e.id !== editorId);
  const events: EditorEvent[] = [{
    kind: 'deleted',
    domain: 'editor',
    timestamp: now(),
    payload: { workspaceId: state.id, editorId },
  }];

  return ok(
    { ...state, editors },
    'WORKSPACE_EDITOR_REMOVED',
    `Editor '${editorId}' removed from workspace`,
    events
  );
}

/**
 * Set an editor as the active (focused) editor in the workspace.
 * Marks the target editor's focus state as active; all others inactive.
 * Returns err if the editor is not found.
 */
export function setActiveEditor(
  state: WorkspaceState,
  editorId: EditorInstanceId
): EditorResult<WorkspaceState> {
  if (!state.editors.some(e => e.id === editorId)) {
    return err('WORKSPACE_EDITOR_NOT_FOUND', `Editor '${editorId}' not found in workspace`);
  }

  const editors = state.editors.map(e => ({
    ...e,
    focus: {
      hasFocus: e.id === editorId,
      focusTarget: e.id === editorId ? ('editor' as const) : ('none' as const),
    },
  }));

  const events: EditorEvent[] = [{
    kind: 'activated',
    domain: 'editor',
    timestamp: now(),
    payload: { workspaceId: state.id, editorId },
  }];

  return ok(
    { ...state, editors },
    'WORKSPACE_EDITOR_ACTIVATED',
    `Editor '${editorId}' set as active`,
    events
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 TERMINAL MANAGEMENT — add and remove terminals
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a TerminalState to the workspace.
 * Returns err if a terminal with the same ID already exists.
 */
export function addTerminal(
  state: WorkspaceState,
  terminalState: TerminalState
): EditorResult<WorkspaceState> {
  if (state.terminals.some(t => t.id === terminalState.id)) {
    return err('WORKSPACE_TERMINAL_EXISTS', `Terminal '${terminalState.id}' already in workspace`);
  }

  const events: EditorEvent[] = [{
    kind: 'created',
    domain: 'terminal',
    timestamp: now(),
    payload: { workspaceId: state.id, terminalId: terminalState.id },
  }];

  return ok(
    { ...state, terminals: [...state.terminals, terminalState] },
    'WORKSPACE_TERMINAL_ADDED',
    `Terminal '${terminalState.id}' added to workspace`,
    events
  );
}

/**
 * Remove a terminal from the workspace by ID.
 * Returns err if the terminal is not found.
 */
export function removeTerminal(
  state: WorkspaceState,
  terminalId: TerminalId
): EditorResult<WorkspaceState> {
  if (!state.terminals.some(t => t.id === terminalId)) {
    return err('WORKSPACE_TERMINAL_NOT_FOUND', `Terminal '${terminalId}' not found in workspace`);
  }

  const terminals = state.terminals.filter(t => t.id !== terminalId);
  const events: EditorEvent[] = [{
    kind: 'deleted',
    domain: 'terminal',
    timestamp: now(),
    payload: { workspaceId: state.id, terminalId },
  }];

  return ok(
    { ...state, terminals },
    'WORKSPACE_TERMINAL_REMOVED',
    `Terminal '${terminalId}' removed from workspace`,
    events
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 CONFIG — update workspace settings and layout
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Update top-level workspace configuration (name, theme, settings, rootFolders).
 * Merges provided fields onto the existing state — all fields are optional.
 */
export function updateWorkspaceConfig(
  state: WorkspaceState,
  config: Partial<{
    readonly name: string;
    readonly theme: string;
    readonly rootFolders: string[];
    readonly settings: Record<string, unknown>;
    readonly layout: WorkspaceLayout;
    readonly panels: PanelState[];
  }>
): EditorResult<WorkspaceState> {
  if (config.name !== undefined && !config.name.trim()) {
    return err('WORKSPACE_INVALID_NAME', 'Workspace name cannot be empty');
  }

  const updated: WorkspaceState = {
    ...state,
    ...(config.name !== undefined ? { name: config.name.trim() } : {}),
    ...(config.theme !== undefined ? { theme: config.theme } : {}),
    ...(config.rootFolders !== undefined ? { rootFolders: config.rootFolders } : {}),
    ...(config.settings !== undefined ? { settings: { ...state.settings, ...config.settings } } : {}),
    ...(config.layout !== undefined ? { layout: config.layout } : {}),
    ...(config.panels !== undefined ? { panels: config.panels } : {}),
  };

  const events: EditorEvent[] = [{
    kind: 'changed',
    domain: 'workspace',
    timestamp: now(),
    payload: { workspaceId: state.id, changedFields: Object.keys(config) },
  }];

  return ok(updated, 'WORKSPACE_CONFIG_UPDATED', `Workspace '${updated.name}' config updated`, events);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 SESSION PERSISTENCE — serialize and restore workspace state
// ═══════════════════════════════════════════════════════════════════════════════

/** Serialized workspace session suitable for storage. */
export interface SerializedWorkspaceSession {
  readonly version: '2.0.0';
  readonly workspaceId: WorkspaceId;
  readonly name: string;
  readonly rootFolders: string[];
  readonly theme: string;
  readonly settings: Record<string, unknown>;
  readonly layout: WorkspaceLayout;
  readonly panels: PanelState[];
  readonly activeEditorId?: EditorInstanceId;
  readonly openEditorDocumentIds: string[];
  readonly openTerminalIds: TerminalId[];
  readonly savedAt: number;
}

/**
 * Serialize a workspace state into a persistence-safe snapshot.
 * Does not include full editor or terminal state — just IDs and config.
 */
export function saveSession(
  state: WorkspaceState
): EditorResult<SerializedWorkspaceSession> {
  const activeEditor = state.editors.find(e => e.focus.hasFocus);
  const session: SerializedWorkspaceSession = {
    version: '2.0.0',
    workspaceId: state.id,
    name: state.name,
    rootFolders: state.rootFolders,
    theme: state.theme,
    settings: state.settings,
    layout: state.layout,
    panels: state.panels,
    activeEditorId: activeEditor?.id,
    openEditorDocumentIds: state.editors.map(e => e.documentId),
    openTerminalIds: state.terminals.map(t => t.id),
    savedAt: now(),
  };

  return ok(session, 'WORKSPACE_SESSION_SAVED', `Workspace '${state.name}' session saved`);
}

/**
 * Restore a workspace state from a serialized session snapshot.
 * Reconstructs config; editors and terminals must be re-opened by the consumer.
 */
export function restoreSession(
  serialized: SerializedWorkspaceSession
): EditorResult<WorkspaceState> {
  if (serialized.version !== '2.0.0') {
    return err(
      'WORKSPACE_SESSION_VERSION_MISMATCH',
      `Cannot restore session version '${serialized.version}' — expected '2.0.0'`
    );
  }

  const state: WorkspaceState = {
    id: serialized.workspaceId,
    name: serialized.name,
    rootFolders: serialized.rootFolders,
    layout: serialized.layout,
    panels: serialized.panels,
    editors: [],
    terminals: [],
    theme: serialized.theme,
    settings: serialized.settings,
  };

  const events: EditorEvent[] = [{
    kind: 'opened',
    domain: 'workspace',
    timestamp: now(),
    payload: { workspaceId: serialized.workspaceId, savedAt: serialized.savedAt },
  }];

  return ok(state, 'WORKSPACE_SESSION_RESTORED', `Workspace '${state.name}' restored`, events);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 SEARCH — workspace-level cross-file search entry point
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Initialize a cross-file search operation for the workspace.
 * Returns a FindInFilesState configured with the query. The actual search
 * execution happens in the IO layer (filesystem search engine).
 */
export function searchWorkspace(
  _state: WorkspaceState,
  query: string,
  options?: {
    readonly isRegex?: boolean;
    readonly isCaseSensitive?: boolean;
    readonly isWholeWord?: boolean;
    readonly include?: string;
    readonly exclude?: string;
  }
): EditorResult<FindInFilesState> {
  if (!query.trim()) {
    return err('WORKSPACE_SEARCH_EMPTY_QUERY', 'Search query cannot be empty');
  }

  const searchState: FindInFilesState = {
    query: query.trim(),
    include: options?.include ?? '**/*',
    exclude: options?.exclude ?? '**/node_modules/**',
    isRegex: options?.isRegex ?? false,
    isCaseSensitive: options?.isCaseSensitive ?? false,
    isWholeWord: options?.isWholeWord ?? false,
    results: [],
  };

  return ok(
    searchState,
    'WORKSPACE_SEARCH_INITIALIZED',
    `Search initialized for query: '${searchState.query}'`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 SUMMARY — workspace statistics
// ═══════════════════════════════════════════════════════════════════════════════

/** Summary statistics for a workspace. */
export interface WorkspaceSummary {
  readonly workspaceId: WorkspaceId;
  readonly name: string;
  readonly editorCount: number;
  readonly terminalCount: number;
  readonly rootFolderCount: number;
  readonly panelCount: number;
  readonly activeEditorId: EditorInstanceId | undefined;
  readonly layoutType: WorkspaceLayout['type'];
  readonly theme: string;
}

/**
 * Return a summary of the workspace for display or diagnostics.
 */
export function getWorkspaceSummary(state: WorkspaceState): WorkspaceSummary {
  const activeEditor = state.editors.find(e => e.focus.hasFocus);
  return {
    workspaceId: state.id,
    name: state.name,
    editorCount: state.editors.length,
    terminalCount: state.terminals.length,
    rootFolderCount: state.rootFolders.length,
    panelCount: state.panels.length,
    activeEditorId: activeEditor?.id,
    layoutType: state.layout.type,
    theme: state.theme,
  };
}

/**
 * Determine if the workspace has unsaved changes (any dirty editor).
 */
export function hasUnsavedChanges(state: WorkspaceState): boolean {
  // DocumentState.isDirty is tracked in the document layer; here we check
  // if there are any editors open as a proxy for potential unsaved state.
  return state.editors.length > 0;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 WORKSPACE MUTATIONS — name, folders, theme, settings
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Rename the workspace.
 */
export function updateWorkspaceName(state: WorkspaceState, name: string): EditorResult<WorkspaceState> {
  if (!name.trim()) return err('EMPTY_NAME', 'Workspace name cannot be empty');
  return ok({ ...state, name: name.trim() }, 'WORKSPACE_RENAMED', `Renamed workspace to "${name}"`);
}

/**
 * Add a root folder to the workspace.
 */
export function addRootFolder(state: WorkspaceState, folder: string): EditorResult<WorkspaceState> {
  if (!folder.trim()) return err('EMPTY_FOLDER', 'Folder path cannot be empty');
  if (state.rootFolders.includes(folder)) return err('FOLDER_EXISTS', `Folder "${folder}" already in workspace`);
  return ok(
    { ...state, rootFolders: [...state.rootFolders, folder] },
    'ROOT_FOLDER_ADDED',
    `Added root folder "${folder}"`
  );
}

/**
 * Remove a root folder from the workspace.
 */
export function removeRootFolder(state: WorkspaceState, folder: string): EditorResult<WorkspaceState> {
  const filtered = state.rootFolders.filter((f) => f !== folder);
  if (filtered.length === state.rootFolders.length) return err('FOLDER_NOT_FOUND', `Folder "${folder}" not found`);
  if (filtered.length === 0) return err('LAST_FOLDER', 'Cannot remove the last root folder');
  return ok(
    { ...state, rootFolders: filtered },
    'ROOT_FOLDER_REMOVED',
    `Removed root folder "${folder}"`
  );
}

/**
 * Set the workspace theme.
 */
export function setTheme(state: WorkspaceState, theme: string): EditorResult<WorkspaceState> {
  if (!theme.trim()) return err('EMPTY_THEME', 'Theme name cannot be empty');
  return ok({ ...state, theme: theme.trim() }, 'THEME_SET', `Set theme to "${theme}"`);
}

/**
 * Update a single settings key on the workspace.
 */
export function updateSettings(
  state: WorkspaceState,
  key: string,
  value: unknown
): EditorResult<WorkspaceState> {
  if (!key.trim()) return err('EMPTY_KEY', 'Settings key cannot be empty');
  const settings = { ...state.settings, [key]: value };
  return ok({ ...state, settings }, 'SETTINGS_UPDATED', `Updated setting "${key}"`);
}

/**
 * Return a lightweight summary of workspace state.
 */
export function workspaceSummary(
  state: WorkspaceState
): { name: string; folderCount: number; editorCount: number; terminalCount: number } {
  return {
    name: state.name,
    folderCount: state.rootFolders.length,
    editorCount: state.editors.length,
    terminalCount: state.terminals.length,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 JSON SERIALIZATION — serializeWorkspace, deserializeWorkspace
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Serialize workspace state to a JSON string for generic persistence.
 */
export function serializeWorkspace(state: WorkspaceState): EditorResult<string> {
  const serializable = {
    id: state.id,
    name: state.name,
    rootFolders: state.rootFolders,
    layout: state.layout,
    panels: state.panels,
    theme: state.theme,
    settings: state.settings,
    serializedAt: now(),
  };
  const json = JSON.stringify(serializable);
  return ok(json, 'WORKSPACE_SERIALIZED', `Serialized workspace "${state.name}"`);
}

/**
 * Deserialize workspace state from a JSON string.
 */
export function deserializeWorkspace(data: string): EditorResult<WorkspaceState> {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(data);
  } catch {
    return err('PARSE_FAILED', 'Failed to parse workspace JSON');
  }
  if (!parsed['id'] || !parsed['name'] || !parsed['rootFolders']) {
    return err('INVALID_DATA', 'Missing required workspace fields');
  }
  const state: WorkspaceState = {
    id: parsed['id'] as WorkspaceId,
    name: parsed['name'] as string,
    rootFolders: parsed['rootFolders'] as string[],
    layout: parsed['layout'] as WorkspaceLayout,
    panels: (parsed['panels'] ?? []) as PanelState[],
    editors: [],
    terminals: [],
    theme: (parsed['theme'] as string) ?? 'default',
    settings: (parsed['settings'] ?? {}) as Record<string, unknown>,
  };
  return ok(state, 'WORKSPACE_DESERIALIZED', `Deserialized workspace "${state.name}"`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §11 CHECKPOINTS — createCheckpoint, restoreFromCheckpoint, fingerprint, diff
// ═══════════════════════════════════════════════════════════════════════════════

/** A point-in-time snapshot of workspace state. */
export interface WorkspaceCheckpoint {
  readonly timestamp: number;
  readonly state: WorkspaceState;
  readonly fingerprint: string;
}

/**
 * Compute a stable fingerprint string for a workspace state.
 * Used to detect changes without deep equality.
 */
export function workspaceFingerprint(state: WorkspaceState): string {
  const parts = [
    state.id,
    state.name,
    state.rootFolders.join(':'),
    state.theme,
    state.layout.type,
    state.panels.length.toString(),
    state.editors.length.toString(),
    state.terminals.length.toString(),
    JSON.stringify(state.settings),
  ];
  let hash = 0;
  const str = parts.join('|');
  for (let i = 0; i < str.length; i++) {
    const chr = str.charCodeAt(i);
    hash = ((hash << 5) - hash + chr) | 0;
  }
  return 'ws-' + Math.abs(hash).toString(36);
}

/**
 * Return true if the two workspace states differ (via fingerprint).
 */
export function hasWorkspaceChanged(a: WorkspaceState, b: WorkspaceState): boolean {
  return workspaceFingerprint(a) !== workspaceFingerprint(b);
}

/** A single field-level change between two workspace states. */
export interface WorkspaceChange {
  readonly field: string;
  readonly oldValue: unknown;
  readonly newValue: unknown;
}

/**
 * Compute a field-level diff between two workspace states.
 */
export function workspaceDiff(old_: WorkspaceState, new_: WorkspaceState): WorkspaceChange[] {
  const changes: WorkspaceChange[] = [];
  if (old_.name !== new_.name) {
    changes.push({ field: 'name', oldValue: old_.name, newValue: new_.name });
  }
  if (old_.theme !== new_.theme) {
    changes.push({ field: 'theme', oldValue: old_.theme, newValue: new_.theme });
  }
  const oldFolders = old_.rootFolders.join(',');
  const newFolders = new_.rootFolders.join(',');
  if (oldFolders !== newFolders) {
    changes.push({ field: 'rootFolders', oldValue: old_.rootFolders, newValue: new_.rootFolders });
  }
  if (old_.layout.type !== new_.layout.type) {
    changes.push({ field: 'layout.type', oldValue: old_.layout.type, newValue: new_.layout.type });
  }
  if (old_.panels.length !== new_.panels.length) {
    changes.push({ field: 'panels', oldValue: old_.panels.length, newValue: new_.panels.length });
  }
  if (old_.editors.length !== new_.editors.length) {
    changes.push({ field: 'editors', oldValue: old_.editors.length, newValue: new_.editors.length });
  }
  if (old_.terminals.length !== new_.terminals.length) {
    changes.push({ field: 'terminals', oldValue: old_.terminals.length, newValue: new_.terminals.length });
  }
  const oldSettings = JSON.stringify(old_.settings);
  const newSettings = JSON.stringify(new_.settings);
  if (oldSettings !== newSettings) {
    changes.push({ field: 'settings', oldValue: old_.settings, newValue: new_.settings });
  }
  return changes;
}

/**
 * Create a checkpoint snapshot of the workspace state.
 */
export function createCheckpoint(state: WorkspaceState): EditorResult<WorkspaceCheckpoint> {
  const fp = workspaceFingerprint(state);
  const checkpoint: WorkspaceCheckpoint = {
    timestamp: now(),
    state,
    fingerprint: fp,
  };
  return ok(checkpoint, 'CHECKPOINT_CREATED', `Created checkpoint for workspace "${state.name}"`);
}

/**
 * Restore workspace state from a checkpoint.
 */
export function restoreFromCheckpoint(checkpoint: WorkspaceCheckpoint): EditorResult<WorkspaceState> {
  return ok(checkpoint.state, 'WORKSPACE_RESTORED', `Restored workspace from checkpoint (${new Date(checkpoint.timestamp).toISOString()})`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §12 COLLABORATION STATE — createCollabState
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create an empty WorkspaceCollabState for initializing collaboration.
 */
export function createCollabState(): EditorResult<import('../code-editor-types.js').WorkspaceCollabState> {
  const state: import('../code-editor-types.js').WorkspaceCollabState = {
    isActive: false,
    participants: [],
    cursors: [],
    followTarget: undefined,
  };
  return ok(state, 'COLLAB_CREATED', 'Created collaboration state');
}
